import React, { useState, useEffect, useRef } from "react";

interface CarouselProps {
    images: string[];
    alt: string;
    timer?: number;
}

const Carousel: React.FC<CarouselProps> = ({ images, alt, timer }) => {
    const [current, setCurrent] = useState(0);
    const [fade, setFade] = useState(true);
    const intervalRef = useRef<NodeJS.Timeout | null>(null);

    // Helper to clear and restart the interval
    const resetInterval = () => {
        if (intervalRef.current) clearInterval(intervalRef.current);
        if (timer && images.length > 1) {
            intervalRef.current = setInterval(() => {
                setFade(false);
                setTimeout(() => {
                    setCurrent((prev) => (prev === images.length - 1 ? 0 : prev + 1));
                    setFade(true);
                }, 150);
            }, timer * 1000);
        }
    };

    const prev = () => {
        setFade(false);
        setTimeout(() => {
            setCurrent((prev) => (prev === 0 ? images.length - 1 : prev - 1));
            setFade(true);
            resetInterval();
        }, 150);
    };

    const next = () => {
        setFade(false);
        setTimeout(() => {
            setCurrent((prev) => (prev === images.length - 1 ? 0 : prev + 1));
            setFade(true);
            resetInterval();
        }, 150);
    };

    // Auto-advance logic
    useEffect(() => {
        resetInterval();
        return () => {
            if (intervalRef.current) clearInterval(intervalRef.current);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [timer, images.length]);

    // Reset to first image if images array changes
    useEffect(() => {
        setCurrent(0);
    }, [images]);

    if (images.length === 0) return null;

    return (
        <div className="relative w-full flex flex-col items-center" style={{ minHeight: "18rem" }}>
            <div className="flex items-center justify-center w-full h-72 lg:h-[400px]">
                <img
                    src={images[current]}
                    alt={alt}
                    className={`rounded-lg object-contain h-72 lg:h-[400px] w-auto transition-opacity duration-200 ${fade ? "opacity-100" : "opacity-0"}`}
                />
            </div>
            {images.length > 1 && (
                <>
                    <button
                        onClick={prev}
                        className="absolute left-2 top-1/2 -translate-y-1/2 bg-white bg-opacity-80 rounded-full p-2 shadow hover:bg-blue-600 hover:text-white hover:bg-opacity-100 transition flex items-center justify-center text-2xl"
                        aria-label="Previous image"
                        type="button"
                    >
                        <svg width="24" height="24" fill="none" viewBox="0 0 24 24">
                            <circle cx="12" cy="12" r="12" fill="currentColor" className="text-blue-100" />
                            <path d="M14.5 7l-5 5 5 5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-blue-600" />
                        </svg>
                    </button>
                    <button
                        onClick={next}
                        className="absolute right-2 top-1/2 -translate-y-1/2 bg-white bg-opacity-80 rounded-full p-2 shadow hover:bg-blue-600 hover:text-white hover:bg-opacity-100 transition flex items-center justify-center text-2xl"
                        aria-label="Next image"
                        type="button"
                    >
                        <svg width="24" height="24" fill="none" viewBox="0 0 24 24">
                            <circle cx="12" cy="12" r="12" fill="currentColor" className="text-blue-100" />
                            <path d="M9.5 7l5 5-5 5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-blue-600" />
                        </svg>
                    </button>
                </>
            )}
            {/* Dots below the image */}
            {images.length > 1 && (
                <div className="mt-4 flex gap-1">
                    {images.map((_, idx) => (
                        <span
                            key={idx}
                            className={`inline-block w-2 h-2 rounded-full ${idx === current ? "bg-secondary" : "bg-gray-300"}`}
                        />
                    ))}
                </div>
            )}
        </div>
    );
};

export default Carousel;